import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { X, Send, ShieldCheck, Zap, Eye } from "lucide-react";

const MODES = [
  ["read", "Read-only", Eye, "I only look things up"],
  ["suggest", "Ask first", ShieldCheck, "I draft actions, you confirm"],
  ["auto", "Auto", Zap, "I act — critical things still ask"],
];

export default function RadarAI() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(user?.ai_mode || "suggest");
  const [pending, setPending] = useState(null);
  const sessionRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, pending]);

  if (!user) return null;

  const changeMode = async (m) => {
    setMode(m);
    try { await api.post("/ai/mode", { mode: m }); } catch { /* keep local */ }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionRef.current });
      sessionRef.current = data.session_id;
      if (data.action_card) setPending(data.action_card);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, executed: data.executed }]);
    } catch (err) {
      const premium = err?.response?.status === 402;
      setMessages((m) => [...m, { role: "assistant", content: fmtErr(err) + (premium ? "\n\nUpgrade at /pricing — or ask the CourierRadar team to switch it on for you." : "") }]);
    }
    setBusy(false);
  };

  const resolve = async (actionId, confirm) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/actions/${actionId}/${confirm ? "confirm" : "cancel"}`);
      setPending(null);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, executed: confirm ? data.executed : null }]);
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  return (
    <>
      <button data-testid="radar-ai-button" onClick={() => setOpen(!open)}
        className={`group fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center ${open ? "bg-[#1C1B1A] shadow-[0_12px_40px_rgba(28,27,26,0.35)] transition-transform hover:scale-105 active:scale-95" : "radar-fab"}`}
        aria-label="Ask Radar — your AI dispatch copilot">
        {!open && <span className="pointer-events-none absolute right-full mr-3 whitespace-nowrap bg-[#1C1B1A] text-white text-[11px] font-semibold rounded-full px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 hidden sm:block">Ask Radar anything</span>}
        {open ? <X className="w-5 h-5 text-[#F2D06B]" /> : <img src="/radar-ai.png" alt="Radar" className="w-full h-full object-cover rounded-full" />}
      </button>
      {open && (
        <div data-testid="radar-ai-panel"
          className="fixed bottom-20 right-4 sm:right-5 z-50 w-[calc(100vw-2rem)] max-w-sm bg-white border border-[#E8E4D9] rounded-[28px] shadow-[0_20px_60px_rgba(28,27,26,0.18)] flex flex-col overflow-hidden">
          <div className="px-4 py-3 bg-[#1C1B1A] text-white flex items-center gap-2.5">
            <img src="/radar-ai.png" alt="" className="w-6 h-6 rounded-md" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight">Radar</p>
              <p className="text-[10px] text-white/60">Works on your real jobs, bids and documents</p>
            </div>
          </div>
          <div className="flex gap-1 px-3 pt-2.5" data-testid="radar-modes">
            {MODES.map(([v, label, Icon, hint]) => (
              <button key={v} data-testid={`radar-mode-${v}`} onClick={() => changeMode(v)} title={hint}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-full text-[10px] font-semibold transition-all ${mode === v ? "bg-[#1C1B1A] text-white" : "bg-[#F6F3EA] text-[#78756E]"}`}>
                <Icon className="w-3 h-3" />{label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-h-[200px] max-h-[44vh] overflow-y-auto px-3.5 py-3 space-y-2.5" data-testid="radar-ai-messages">
            {messages.length === 0 && (
              <div className="text-xs text-[#78756E] bg-[#F6F3EA] rounded-2xl px-3.5 py-3 leading-relaxed">
                {user.role === "courier" && "Try: “find van jobs in Germany and bid on the best one”, “accept that fixed-price load”, “email me a jobs report every morning”"}
                {user.role === "company" && "Try: “book a van Berlin → Paris, Friday 8am”, “publish this order email as a job”, “compare couriers on CR-… and assign the best”"}
                {user.role === "admin" && "Try: “who needs approval?”, “email me the Control Tower report every morning”, “failed payments”, “stale jobs”"}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-xs leading-relaxed rounded-2xl px-3.5 py-2.5 whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#1C1B1A] text-white ml-8" : "bg-[#F6F3EA] text-[#1C1B1A] mr-8"}`}>
                {m.content}
                {m.executed && <p className="text-[10px] text-[#A09D95] mt-1">✓ done via {m.executed} · logged in audit trail</p>}
              </div>
            ))}
            {busy && <div className="text-xs text-[#A09D95] bg-[#F6F3EA] rounded-2xl px-3.5 py-2.5 mr-8 animate-pulse">Radar is working…</div>}
            <div ref={bottomRef} />
          </div>
          {pending && (
            <div className="mx-3 mb-2 border border-[#1C1B1A] rounded-2xl p-3 bg-[#FCFAF5]" data-testid="radar-action-card">
              <p className="text-[11px] font-bold mb-1">Confirm this action</p>
              <p className="text-[11px] text-[#3f3f46] leading-relaxed mb-2.5">{pending.summary}</p>
              <div className="flex gap-2">
                <button data-testid="radar-confirm-btn" onClick={() => resolve(pending.id, true)} disabled={busy}
                  className="flex-1 bg-[#1C1B1A] text-white text-[11px] font-semibold px-3 py-2 rounded-full hover:bg-[#2C2B2A] transition-colors disabled:opacity-40">Confirm</button>
                <button data-testid="radar-cancel-btn" onClick={() => resolve(pending.id, false)} disabled={busy}
                  className="flex-1 border border-[#E8E4D9] text-[#78756E] text-[11px] font-semibold px-3 py-2 rounded-full hover:bg-[#F6F3EA] transition-colors disabled:opacity-40">Cancel</button>
              </div>
            </div>
          )}
          <form onSubmit={send} className="p-3 border-t border-[#E8E4D9] flex items-center gap-2">
            <input data-testid="radar-ai-input" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Radar…" className="cr-input !rounded-full flex-1 !py-2.5" />
            <button data-testid="radar-ai-send" disabled={busy || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#F2D06B] hover:bg-[#EFCB68] text-[#1C1B1A] flex items-center justify-center transition-colors disabled:opacity-40 shrink-0">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
